import React, { useMemo, useState } from "react";
import {
  BarChart3,
  CheckCircle2,
  CreditCard,
  Database,
  Globe,
  Layers3,
  Lock,
  Mail,
  MessageSquare,
  Search,
  Server,
  Shield,
  Wrench
} from "lucide-react";

type ProviderStatus = "connected" | "platform_managed" | "internal_only" | "not_connected";

interface ProviderRecord {
  name: string;
  status: ProviderStatus;
  category: string;
  helper: string;
  owner: string;
  lastReviewed: string;
  surface: string;
  nextStep: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}

const providers: ProviderRecord[] = [
  {
    name: "Email Delivery",
    status: "connected",
    category: "Communications",
    helper: "Outbound school and platform email is handled through backend environment credentials.",
    owner: "Server environment",
    lastReviewed: "March 10, 2026",
    surface: "Backend configuration",
    nextStep: "Expose delivery health and bounce telemetry once provider metrics are wired.",
    Icon: Mail
  },
  {
    name: "Payments",
    status: "not_connected",
    category: "Billing",
    helper: "No live payment gateway is currently wired into school subscriptions or tenant billing.",
    owner: "Not configured",
    lastReviewed: "March 10, 2026",
    surface: "Subscription workflow",
    nextStep: "Add secure gateway credentials and callback handling before enabling live billing.",
    Icon: CreditCard
  },
  {
    name: "SMS / Messaging",
    status: "not_connected",
    category: "Communications",
    helper: "No SMS provider is configured for OTP, broadcasts, or urgent school alerts.",
    owner: "Not configured",
    lastReviewed: "March 10, 2026",
    surface: "Notifications pipeline",
    nextStep: "Introduce a provider-backed send queue before exposing tenant SMS controls.",
    Icon: MessageSquare
  },
  {
    name: "Storage / Backups",
    status: "platform_managed",
    category: "Infrastructure",
    helper: "Backups are tracked by the superadmin backup console rather than a separate vendor UI.",
    owner: "Platform operations",
    lastReviewed: "March 10, 2026",
    surface: "Backup console",
    nextStep: "Add restore validation status and download provenance to the backup workflow.",
    Icon: Database
  },
  {
    name: "Analytics",
    status: "internal_only",
    category: "Reporting",
    helper: "Financial and operational analytics are sourced from internal dashboards and platform queries.",
    owner: "Internal services",
    lastReviewed: "March 10, 2026",
    surface: "Analytics dashboards",
    nextStep: "Add external event export only if a real analytics sink is introduced.",
    Icon: BarChart3
  },
  {
    name: "Security / SSO",
    status: "not_connected",
    category: "Identity",
    helper: "No external identity provider or SSO bridge is configured for the platform.",
    owner: "Not configured",
    lastReviewed: "March 10, 2026",
    surface: "Authentication system",
    nextStep: "Add provider metadata storage and admin-safe connection testing before rollout.",
    Icon: Shield
  }
];

const statusCopy: Record<ProviderStatus, { label: string; tone: string }> = {
  connected: {
    label: "Connected",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700"
  },
  platform_managed: {
    label: "Platform managed",
    tone: "border-sky-200 bg-sky-50 text-sky-700"
  },
  internal_only: {
    label: "Internal only",
    tone: "border-violet-200 bg-violet-50 text-violet-700"
  },
  not_connected: {
    label: "Not connected",
    tone: "border-slate-200 bg-slate-100 text-slate-600"
  }
};

const Integrations: React.FC = () => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProviderStatus | "all">("all");

  const filteredProviders = useMemo(() => {
    const term = query.trim().toLowerCase();
    return providers.filter((provider) => {
      const matchesStatus = statusFilter === "all" || provider.status === statusFilter;
      const matchesQuery =
        !term ||
        [provider.name, provider.category, provider.helper, provider.owner, provider.surface]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesStatus && matchesQuery;
    });
  }, [query, statusFilter]);

  const stats = useMemo(
    () => ({
      connected: providers.filter((provider) => provider.status === "connected").length,
      managed: providers.filter((provider) => provider.status === "platform_managed").length,
      internal: providers.filter((provider) => provider.status === "internal_only").length,
      pending: providers.filter((provider) => provider.status === "not_connected").length
    }),
    []
  );

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Integrations</p>
              <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Platform provider inventory</h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
                Track which provider surfaces are actually connected, which capabilities are managed internally,
                and which integration gaps still need backend support before any tenant-facing setup UI is safe to expose.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:min-w-[360px]">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Security posture</p>
                <p className="mt-3 text-lg font-black text-slate-900">Secrets remain backend-only</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  No provider credential editing is exposed in the client until there is audited backend storage.
                </p>
              </div>
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Operational guidance</p>
                <p className="mt-3 text-lg font-black text-slate-900">Use this page as inventory</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Connected surfaces are documented here; actual management stays inside the relevant backend and ops flows.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            {
              title: "Connected",
              value: stats.connected,
              helper: "Live provider surfaces",
              Icon: CheckCircle2,
              tone: "border-emerald-200 bg-emerald-50 text-emerald-700"
            },
            {
              title: "Platform Managed",
              value: stats.managed,
              helper: "Handled internally",
              Icon: Server,
              tone: "border-sky-200 bg-sky-50 text-sky-700"
            },
            {
              title: "Internal Only",
              value: stats.internal,
              helper: "No external vendor dependency",
              Icon: Layers3,
              tone: "border-violet-200 bg-violet-50 text-violet-700"
            },
            {
              title: "Pending",
              value: stats.pending,
              helper: "Still require implementation",
              Icon: Wrench,
              tone: "border-amber-200 bg-amber-50 text-amber-700"
            }
          ].map(({ title, value, helper, Icon, tone }) => (
            <article key={title} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">{title}</p>
                  <p className="mt-3 text-3xl font-black tracking-tight text-slate-950">{value}</p>
                  <p className="mt-2 text-sm text-slate-500">{helper}</p>
                </div>
                <div className={`rounded-2xl border p-3 ${tone}`}>
                  <Icon size={20} />
                </div>
              </div>
            </article>
          ))}
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Provider Search</p>
              <h2 className="mt-2 text-xl font-black text-slate-950">Inspect connected and pending surfaces</h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search providers, owners, or categories"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white sm:w-[320px]"
                />
              </label>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as ProviderStatus | "all")}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-slate-300 focus:bg-white"
              >
                <option value="all">All statuses</option>
                <option value="connected">Connected</option>
                <option value="platform_managed">Platform managed</option>
                <option value="internal_only">Internal only</option>
                <option value="not_connected">Not connected</option>
              </select>
            </div>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredProviders.map((provider) => {
            const { Icon } = provider;
            const statusMeta = statusCopy[provider.status];
            return (
              <article key={provider.name} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                    <Icon size={20} />
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] ${statusMeta.tone}`}>
                    {statusMeta.label}
                  </span>
                </div>
                <div className="mt-5">
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">{provider.category}</p>
                  <h2 className="mt-2 text-lg font-black text-slate-900">{provider.name}</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{provider.helper}</p>
                </div>
                <dl className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-slate-500">Managed through</dt>
                    <dd className="text-right font-semibold text-slate-800">{provider.surface}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-slate-500">Owner</dt>
                    <dd className="text-right font-semibold text-slate-800">{provider.owner}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-slate-500">Last reviewed</dt>
                    <dd className="text-right font-semibold text-slate-800">{provider.lastReviewed}</dd>
                  </div>
                </dl>
                <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Next step</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{provider.nextStep}</p>
                </div>
              </article>
            );
          })}
        </section>

        {!filteredProviders.length ? (
          <section className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
              <Search size={20} />
            </div>
            <h2 className="mt-4 text-xl font-black text-slate-900">No providers match the current filters</h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              Clear the search term or switch the status filter to inspect the full platform integration inventory again.
            </p>
          </section>
        ) : null}

        <section className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                <Globe size={18} />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Current Gaps</p>
                <h2 className="text-xl font-black text-slate-950">Highest-priority missing provider surfaces</h2>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {providers
                .filter((provider) => provider.status === "not_connected")
                .map((provider) => (
                  <div key={provider.name} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-bold text-slate-900">{provider.name}</p>
                        <p className="mt-1 text-sm text-slate-500">{provider.category}</p>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] ${statusCopy[provider.status].tone}`}>
                        {statusCopy[provider.status].label}
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">{provider.nextStep}</p>
                  </div>
                ))}
            </div>
          </article>

          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
                <Lock size={18} />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Guardrails</p>
                <h2 className="text-xl font-black text-slate-950">Why this page stays read-only</h2>
              </div>
            </div>
            <ul className="mt-6 space-y-3 text-sm leading-6 text-slate-600">
              <li className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Provider secrets are not stored through a hardened superadmin settings vault yet.
              </li>
              <li className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Connection testing would need backend-safe probes and audit logging before exposing live actions.
              </li>
              <li className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Tenant-facing configuration should only appear after each provider surface has a dedicated backend contract.
              </li>
            </ul>
          </article>
        </section>
      </div>
    </div>
  );
};

export default Integrations;
